/**
 * useEffectiveLanguage — Cluster C-5 closure hook (Feb 06 2026).
 *
 * Locks single-call-site discipline for "what language should this
 * user-typed message be POSTed as?" by combining `useLanguage()` with
 * `resolveLanguage(text, uiLanguage)` in one tiny hook.
 *
 * Background: iter87 RCA revealed that the cardinal user-reported
 * bug (AR text typed in EN UI → English AML response) reappeared
 * because `useAgentChat.js` was still POSTing the bare UI signal
 * even after `useDrafting.js` was migrated to the shared resolver.
 * The fix at iter88 added an inline `resolveLanguage(message, language)`
 * at the POST builder, but that pattern is easy to re-skip in a new
 * send path. This hook enforces the right thing by construction:
 * call sites get a single `getEffectiveLanguage(text)` function that
 * cannot be passed raw `language` by accident.
 *
 * Usage:
 *   const getEffectiveLanguage = useEffectiveLanguage();
 *   // ... later in a send handler:
 *   body: JSON.stringify({ message, language: getEffectiveLanguage(message), ... })
 *
 * The hook intentionally returns a function (not a value) so a single
 * `getEffectiveLanguage` instance covers an entire send path that
 * may evaluate multiple texts (e.g., chat prompt + document_text).
 */
import { useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { resolveLanguage } from '@/lib/resolveLanguage';

export const useEffectiveLanguage = () => {
  const { language } = useLanguage();
  return useCallback(
    (text) => resolveLanguage(text, language),
    [language],
  );
};

export default useEffectiveLanguage;
